<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            La resolución y el tamaño de los archivos de tus fotos 360° para
            tours virtuales determinan la nitidez en distintos dispositivos, la
            velocidad de carga y si las imágenes cumplen los requisitos de
            plataformas como Google Street View, Kuula y Travvir. Usar la
            resolución incorrecta te cuesta calidad (demasiado baja) o velocidad
            y almacenamiento (demasiado alta).
          </p>
        </section>

        <section>
          <h2>Por qué las imágenes 360° necesitan mayor resolución</h2>
          <p>
            Una foto estándar se muestra como imagen plana: en un monitor 4K
            toda la imagen llena la pantalla. Una foto 360° se muestra de forma
            diferente: el visor muestra solo una porción de la imagen total —
            aproximadamente el 25 % del ancho para un campo visual de 90°. Esto
            significa que una foto 360° necesita
            <strong>4× la resolución</strong>
            de una foto estándar equivalente para verse igual de nítida.
          </p>
          <p>
            El mínimo recomendado es <strong>8 000 × 4 000 píxeles</strong>
            (32 megapíxeles) para un resultado nítido en visualización normal.
          </p>

          <h3>El ratio de aspecto equirectangular</h3>
          <p>
            Las fotos 360° en proyección equirectangular tienen siempre un
            <strong>ratio 2:1</strong> — el doble de ancho que de alto. Tamaños
            estándar: 4 096 × 2 048 (mínimo), 8 192 × 4 096 (recomendado), 11
            000 × 5 500 (alta calidad profesional).
          </p>
        </section>

        <section>
          <h2>Recomendaciones por plataforma</h2>
          <table>
            <thead>
              <tr>
                <th>Plataforma</th>
                <th>Resolución recomendada</th>
                <th>Tamaño máximo de archivo</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Travvir</td>
                <td>8 000 × 4 000 px</td>
                <td>50 MB</td>
              </tr>
              <tr>
                <td>Google Street View</td>
                <td>7 168 × 3 584 px</td>
                <td>75 MB</td>
              </tr>
              <tr>
                <td>Kuula</td>
                <td>8 000 × 4 000 px</td>
                <td>100 MB</td>
              </tr>
              <tr>
                <td>Facebook 360</td>
                <td>6 000 × 3 000 px</td>
                <td>45 MB</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Resolución producida por smartphones actuales</h2>
          <ul>
            <li>
              <strong>iPhone 15 Pro</strong> — hasta 12 000 × 6 000 px con
              Travvir
            </li>
            <li>
              <strong>Samsung Galaxy S24 Ultra</strong> — hasta 10 000 × 5 000
              px
            </li>
            <li><strong>Google Pixel 8</strong> — hasta 8 000 × 4 000 px</li>
          </ul>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>¿Hay que comprimir las imágenes 360° antes de publicarlas?</h3>
          <p>
            No, Travvir gestiona automáticamente la compresión para la entrega
            web conservando el archivo de alta resolución para exportaciones.
          </p>

          <h3>¿Qué diferencia hay entre JPEG y PNG para fotos 360°?</h3>
          <p>
            El JPEG es el formato recomendado: excelente relación
            calidad/tamaño. El PNG produce archivos 3× más pesados sin mejora
            visual perceptible en los visores 360°.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/corregir-fotos-360-borrosas">Fotos 360° Borrosas: 6 Causas y Soluciones Concretas 2026</a></li>
          <li><a href="/blog/es/que-es-foto-360">¿Qué es una Foto 360°? Guía Completa 2026</a></li>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/integrar-tour-virtual-sitio-web">Cómo Integrar un Tour Virtual 360° en tu Sitio Web 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "tamano-imagen-tour-virtual",
);

useBlogSeo({
  title:
    "Tamaño y Resolución de Imágenes para Tours Virtuales 360° 2026 | Travvir",
  description:
    "Especificaciones de resolución para tours virtuales 360°: resoluciones recomendadas por plataforma (Travvir, Google Street View, Kuula), ratio 2:1 y formatos de archivo.",
  keywords:
    "resolución foto 360, tamaño imagen tour virtual, especificaciones foto 360, píxeles foto 360, formato imagen tour virtual",
  slug: "tamano-imagen-tour-virtual",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Hay que comprimir las imágenes 360° antes de publicarlas?",
      answer:
        "No, Travvir gestiona automáticamente la compresión conservando el archivo de alta resolución para exportaciones.",
    },
    {
      question: "¿Qué diferencia hay entre JPEG y PNG para fotos 360°?",
      answer:
        "El JPEG es el formato recomendado con excelente relación calidad/tamaño. El PNG produce archivos 3× más pesados sin mejora visible.",
    },
  ],
});

</script>
